import gql from 'graphql-tag';
import { GraphQLError, GraphQLFormattedError } from 'graphql';
import {
  ApolloClient,
  NormalizedCacheObject,
  ApolloError,
  TypedDocumentNode,
} from '@apollo/client';
import { FastifyLoggerInstance } from 'fastify';
import {
  costGuideVariables,
  costGuide as costGuideQueryType,
  costGuide_costGuide_CostGuideSuccess_costGuide as costGuideType,
  costGuide_costGuide_CostGuideError_errors as costGuideErrorsType,
} from '@/__generated__/costGuide';
import { apolloClientQuery } from './utils';

const COST_GUIDE: TypedDocumentNode<costGuideQueryType, costGuideVariables> = gql`
  query costGuide($id: ID!) {
    costGuide(id: $id) {
      __typename
      ... on CostGuideSuccess {
        costGuide {
          category {
            categoryName {
              categoryNamePluralLowerCase
              categoryNamePluralTitleCase
              categoryNameSingularLowerCase
              categoryNameSingularTitleCase
            }
          }
          location {
            city
            state {
              name
              stateCode
            }
          }
          meta {
            canonicalURL
            description
            title
          }
          primaryHeadline
          rates {
            averageRate {
              averageHourlyRate {
                rate {
                  amount
                  currencyCode
                }
                rateDuration
              }
              averageMonthlyRate {
                rate {
                  amount
                  currencyCode
                }
                rateDuration
              }
              averageWeeklyRate {
                rate {
                  amount
                  currencyCode
                }
                rateDuration
              }
            }
            lastUpdatedDate
            maximumRate {
              maximumHourlyRate {
                rate {
                  amount
                  currencyCode
                }
                rateDuration
              }
              maximumMonthlyRate {
                rate {
                  amount
                  currencyCode
                }
                rateDuration
              }
              maximumWeeklyRate {
                rate {
                  amount
                  currencyCode
                }
                rateDuration
              }
            }
          }
          searchDefaults {
            location {
              location {
                city
                state {
                  name
                  stateCode
                }
              }
              slug
            }
            service {
              name
              serviceId
              slug
            }
          }
          service {
            czenServiceId
            serviceIdForMember
          }
        }
      }
      ... on CostGuideError {
        errors {
          message
        }
      }
    }
  }
`;

type CostGuideQuery = (
  client: ApolloClient<NormalizedCacheObject>,
  log: FastifyLoggerInstance,
  variables: costGuideVariables
) => Promise<
  | {
      costGuide: costGuideType | null;
      error: null;
    }
  | {
      costGuide: null;
      error:
        | ApolloError
        | readonly GraphQLError[]
        | readonly GraphQLFormattedError[]
        | costGuideErrorsType[];
    }
>;

const costGuide: CostGuideQuery = async (client, log, variables) => {
  const { queryResponse, error } = await apolloClientQuery(client, COST_GUIDE, variables);

  // unknown error
  if (error) {
    return {
      costGuide: null,
      error,
    };
  }

  const { data, errors, error: responseError } = queryResponse;

  // GraphQL errors that occur while processing a GraphQL operation (resulting in built-in error codes such as INTERNAL_SERVER_ERROR)
  if (errors && errors.length > 0) {
    return {
      costGuide: null,
      error: errors,
    };
  }

  // Coverage for 'error' property in queryResponse
  if (responseError) {
    return {
      costGuide: null,
      error: responseError,
    };
  }

  // known error
  if (data.costGuide.__typename === 'CostGuideError') {
    log.error(`[GraphQL errors]: ${errors}`);

    return {
      costGuide: null,
      error: data.costGuide.errors,
    };
  }

  return { costGuide: data.costGuide.costGuide, error: null };
};

export { COST_GUIDE, costGuide };
